import type { SigningField } from '../types/fields';
import { isSignatureFontFamily, resolveFieldTextStyle, toPreviewCss } from './fontMapping';
import { fieldTextLayoutMode } from './textLayout';

function placeholderForType(type: SigningField['type']): string {
  switch (type) {
    case 'signature-type':
      return 'Signature';
    case 'date':
      return 'MM/DD/YYYY';
    case 'name':
      return 'Name';
    case 'initials':
      return 'AA';
    default:
      return 'Text';
  }
}

function buildFontString(css: ReturnType<typeof toPreviewCss>): string {
  return `${css.fontStyle} ${css.fontWeight} ${css.fontSize} ${css.fontFamily}`;
}

function applyMeasureLetterSpacing(ctx: CanvasRenderingContext2D, letterSpacing: string): void {
  if (letterSpacing === 'normal') return;
  const spaced = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in spaced) spaced.letterSpacing = letterSpacing;
}

/** Break long unbroken strings so they can wrap inside a max width. */
export function wrapTextToWidth(
  text: string,
  maxWidthPx: number,
  measure: (line: string) => number
): string[] {
  // BUG-15: strip a single trailing newline so the measured box height matches
  // what the textarea shows (prevents a phantom empty line at the bottom).
  const normalized = text.endsWith('\n') ? text.slice(0, -1) : text;
  if (!normalized) return [''];
  const paragraphs = normalized.split(/\r?\n/);
  const lines: string[] = [];

  const pushBrokenChars = (word: string) => {
    // Memoize each character's measured width and accumulate the chunk width
    // incrementally, so we measure every distinct character at most once instead
    // of re-measuring the full growing prefix on each step. The old code was
    // O(L^2) measureText work per broken line, ~O(M^2) for a single long
    // no-space token in a wide box; this is O(M) constant-length measure calls.
    const charWidthCache = new Map<string, number>();
    const charWidth = (char: string): number => {
      let w = charWidthCache.get(char);
      if (w === undefined) {
        w = measure(char);
        charWidthCache.set(char, w);
      }
      return w;
    };
    let chunk = '';
    let chunkWidth = 0;
    for (const char of word) {
      const cw = charWidth(char);
      // Move the char to a new line only when the running chunk width plus this
      // char exceeds the max and the current chunk is non-empty (a char wider
      // than maxWidthPx on its own still gets its own line, matching the
      // original `&& chunk` guard).
      if (chunkWidth + cw > maxWidthPx && chunk) {
        lines.push(chunk);
        chunk = char;
        chunkWidth = cw;
      } else {
        chunk += char;
        chunkWidth += cw;
      }
    }
    if (chunk) lines.push(chunk);
  };

  for (const para of paragraphs) {
    const words = para.split(/\s+/).filter(Boolean);
    let current = '';
    for (const word of words) {
      if (measure(word) > maxWidthPx) {
        if (current) {
          lines.push(current);
          current = '';
        }
        pushBrokenChars(word);
        continue;
      }
      const test = current ? `${current} ${word}` : word;
      if (measure(test) > maxWidthPx && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    if (words.length === 0 && para === '') lines.push('');
  }
  return lines.length ? lines : [''];
}

// Single reusable offscreen measuring context, lazily created once. It is never
// attached to the DOM, so a module-level singleton is safe and avoids allocating
// a fresh canvas + 2D context on every measure call inside the per-keystroke
// wrapTextToWidth / measureTextFieldScreenBox loops.
let measureCtx: CanvasRenderingContext2D | null | undefined;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureCtx !== undefined) return measureCtx;
  if (typeof document === 'undefined') {
    measureCtx = null;
    return null;
  }
  const canvas = document.createElement('canvas');
  measureCtx = canvas.getContext('2d');
  return measureCtx;
}

export function measureTextWidthPx(
  text: string,
  css: ReturnType<typeof toPreviewCss>
): number {
  const ctx = getMeasureContext();
  if (!ctx) {
    const size = parseInt(css.fontSize, 10) || 13;
    return text.length * size * 0.55;
  }
  ctx.font = buildFontString(css);
  // Reset letter-spacing every call: applyMeasureLetterSpacing leaves a prior
  // non-'normal' value in place on the shared context, so clear it first.
  const spaced = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in spaced) spaced.letterSpacing = 'normal';
  applyMeasureLetterSpacing(ctx, css.letterSpacing);
  let width = ctx.measureText(text).width;
  // Cursive/web fonts can measure narrower than SVG render before the face is ready.
  if (css.fontFamily.toLowerCase().includes('vibes') || css.fontFamily.toLowerCase().includes('script')) {
    width *= 1.08;
  }
  return width;
}

const DEFAULT_MAX_LINE_WIDTH_PX = 420;

/** Screen-pixel box that hugs the rendered text (used by inline editor). */
export function measureTextFieldScreenBox(
  field: SigningField,
  rawText: string,
  displayScale: number,
  maxLineWidthPx = DEFAULT_MAX_LINE_WIDTH_PX * displayScale
): { width: number; height: number; displayScale: number } {
  const value = rawText ?? '';
  const layoutMode = fieldTextLayoutMode(field.type, {
    boxHeight: field.height,
    value: value || field.value,
  });
  const style = resolveFieldTextStyle(field, layoutMode);
  const css = toPreviewCss(style, displayScale);
  const fontSizePx = style.fontSize * displayScale;
  const lineHeightPx = fontSizePx * style.lineHeight;
  const padX = 4 * displayScale;
  const padY = 2 * displayScale;
  const isMultiline = field.type === 'text' && /[\r\n]/.test(value);
  const measure = (line: string) => measureTextWidthPx(line, css);

  if (isMultiline) {
    const lines = value.split(/\r?\n/);
    const lineWidths = lines.map((line) => measure(line.length > 0 ? line : ' '));
    const width = Math.ceil(Math.max(64, ...lineWidths, 24) + padX * 2);
    const height = Math.ceil(Math.max(lineHeightPx, lines.length * lineHeightPx) + padY * 2);
    return { width, height, displayScale };
  }

  const displayText = value.length > 0 ? value : placeholderForType(field.type);
  const singleWidth = measure(displayText);
  // Name/date/initials/signature stay on one line — wrapping breaks form-line preview.
  const isStructuredCompact =
    field.type === 'name' ||
    field.type === 'date' ||
    field.type === 'initials' ||
    field.type === 'signature-type';
  const needsWrap =
    !isStructuredCompact && singleWidth + padX * 2 > maxLineWidthPx;
  const lines = needsWrap
    ? wrapTextToWidth(displayText, maxLineWidthPx - padX * 2, measure)
    : [displayText];
  const lineWidths = lines.map((line) => measure(line));
  const minWidth = Math.max(40, fontSizePx * 2);
  const width = Math.ceil(
    Math.max(minWidth, needsWrap ? maxLineWidthPx : Math.max(...lineWidths) + padX * 2)
  );
  const height = Math.ceil(Math.max(lineHeightPx, lines.length * lineHeightPx) + padY * 2);

  return { width, height, displayScale };
}

/** PDF-point box for persisting tight field dimensions after edit. */
export function measureTextFieldPdfSize(
  field: SigningField,
  rawText: string,
  maxLineWidthPx?: number
): { width: number; height: number } {
  const screen = measureTextFieldScreenBox(field, rawText, 1, maxLineWidthPx);
  return {
    width: Math.max(18, Math.round(screen.width)),
    height: Math.max(18, Math.round(screen.height)),
  };
}

export function usesScriptFont(field: SigningField): boolean {
  const layoutMode = fieldTextLayoutMode(field.type, {
    boxHeight: field.height,
    value: field.value,
  });
  const style = resolveFieldTextStyle(field, layoutMode);
  return isSignatureFontFamily(style.fontFamily);
}